<template>
  <!-- List of applications -->
  <el-scrollbar max-height="100%">
    <div v-auto-animate class="application-grid">
      <div
        v-for="item in applicationList"
        :key="getApplicationIdentifier(item)"
        :class="{
          'item-is-active': isCardActive(getApplicationIdentifier(item)),
          item: true,
        }"
        @click="handleIntegrationCardClick(item)">
        <div class="item-image-wrapper">
          <ApplicationIcon
            :application-name="getApplicationName(item)"
            :size="50" />
        </div>
        <div class="item-text">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="highlightSearchSubstring(getDisplayName(item))" />
          <span
            v-if="(item as SoftwareInformation).subtitle"
            class="item-subtext">
            ({{ (item as SoftwareInformation).subtitle }})</span
          >
        </div>
      </div>
    </div>
  </el-scrollbar>
</template>

<script
  lang="ts"
  setup
  generic="T extends SoftwareMiniOut | SoftwareInformation">
  import { PropType } from 'vue'

  import { SoftwareInformation, SoftwareMiniOut } from '@/client'

  const props = withDefaults(
    defineProps<{
      applicationList: T[]
      highlightText?: string

      singleSelect?: boolean
    }>(),
    { highlightText: '' }
  )

  const emit = defineEmits<{
    (e: 'applicationSelected', app: T): void
    (e: 'applicationDeselected', app: T): void
  }>()

  const selectedApplication = defineModel('selectedApplication', {
    type: Object as PropType<T[]>,
    default: [],
  })

  function isInformation(
    app: SoftwareInformation | SoftwareMiniOut
  ): app is SoftwareInformation {
    return (app as SoftwareInformation).name !== undefined
  }

  function getApplicationName(app: T) {
    if (isInformation(app)) return app.name
    return app.software_name
  }

  function getDisplayName(app: T): string {
    if (isInformation(app)) return app.display_name
    return app.display_name || app.software_name
  }

  function getApplicationIdentifier(app: T) {
    if (isInformation(app)) return app.name + app.type
    return app._id
  }

  function handleIntegrationCardClick(item: T) {
    // check if software was selected
    const identifierOfParamApp = getApplicationIdentifier(item)
    const identifiersOfSelectedApp = selectedApplication.value.map((s) =>
      getApplicationIdentifier(s)
    )

    // if software was selected, deselect it
    if (identifiersOfSelectedApp.includes(identifierOfParamApp)) {
      selectedApplication.value = selectedApplication.value.filter(
        (s) => getApplicationIdentifier(s) != identifierOfParamApp
      )
      emit('applicationDeselected', item)
    }
    // else select it
    else {
      // if single select, remove item before adding a new
      if (props.singleSelect) selectedApplication.value.pop()
      selectedApplication.value.push(item)
      emit('applicationSelected', item)
    }
  }

  // Highlight the active card
  function isCardActive(identifier: string) {
    const getId = getApplicationIdentifier
    // looks better if it is a one-liner
    const selectedIdentifiers = selectedApplication.value.map((s) => getId(s))
    return selectedIdentifiers.includes(identifier)
  }

  // For highlighting search substring
  function highlightSearchSubstring(text: string) {
    const searchText = props.highlightText.toLowerCase()
    const lowerText = text.toLowerCase()
    const index = lowerText.indexOf(searchText)

    const escapedText = escapeHTML(text)

    if (index === -1) return escapedText

    return (
      escapedText.substring(0, index) +
      '<span class="highlight">' +
      escapedText.substring(index, index + searchText.length) +
      '</span>' +
      escapedText.substring(index + searchText.length)
    )
  }

  function escapeHTML(text: string) {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }
</script>

<style scoped lang="scss">
  .item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    border-radius: 5px;
    background-color: var(--el-border-color-extra-lighter);
    border: 1px solid var(--el-border-color);
    cursor: pointer;
    padding: 10px 0 10px 0;
    transition: all 0.1s ease-in-out;

    &:hover {
      .item-image-wrapper {
        box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.3);
        scale: 1.02;
      }
    }

    &-image-wrapper {
      width: 65px;
      height: 65px;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: white;
      border-radius: 8px;
      margin-bottom: 5px;
      // Box shadow for logos with transparent background
      box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.15);
      transition: all 0.1s ease-in-out;
    }

    &-is-active {
      background-color: var(--el-color-primary-light-9);
      border: 1px solid var(--el-color-primary);

      .item-image-wrapper {
        box-shadow: 0px 0px 8px rgba(0, 0, 0, 0.3);
        scale: 1.05;
      }

      .item-text {
        font-weight: 600;
        margin: 0;
      }
    }

    &-text {
      text-align: center;
      transition: all 0.2s ease-in-out;
      padding: 5px 0;
    }
  }

  .application-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    grid-auto-rows: 1fr;
    grid-gap: 15px;

    max-width: 100%;
    height: 100%;
    padding-bottom: 40px;
  }
</style>

<style>
  .highlight {
    color: var(--el-color-primary);
  }
</style>
